import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useBranches } from '@/Pages/Operations/shared';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import SecretRevealDialog from './SecretRevealDialog';
import {
  WEBHOOKS_PERMISSIONS,
  WEBHOOKS_ROUTES,
  WEBHOOK_ROTATION_DEFAULT_OVERLAP_MINUTES,
  WEBHOOK_ROTATION_MAX_OVERLAP_MINUTES,
  invalidateWebhookDeliveries,
  invalidateWebhookEndpoint,
  useWebhookDeliveriesQuery,
  useWebhookDeliveryQuery,
  useWebhookEndpointHealthQuery,
  useWebhookEndpointQuery,
  useWebhookEventTypesQuery,
  type RotateWebhookSecretBody,
  type UpdateWebhookEndpointBody,
  type WebhookEndpoint,
  type WebhookEndpointWithSecret,
} from '../webhooksApi';
import { apiMessage, errorCode, formatDateTime, toneForWebhookDelivery, toneForWebhookEndpoint } from '../lib';

/**
 * One endpoint, opened from its row — overview, secret rotation, a test
 * send, its own delivery log, and (when the platform switched it off) a
 * plain-English answer to "why is this disabled and what do I do".
 */
export default function WebhookEndpointDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { confirm } = useConfirm();
  const { can } = usePermission();
  const canManage = can(WEBHOOKS_PERMISSIONS.manage);

  const { data, isFetching, isError, error, refetch } = useWebhookEndpointQuery(id);
  const reload = () => {
    refetch();
    dispatch(invalidateWebhookEndpoint(id));
  };

  const [rotating, setRotating] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<WebhookEndpointWithSecret | null>(null);

  const toggleEnabled = async () => {
    if (!data) return;
    try {
      if (data.status === 'ACTIVE') {
        await api.post(WEBHOOKS_ROUTES.endpointDisable(id));
        toast.success(t('integrations.webhooks.disabled'));
      } else {
        await api.post(WEBHOOKS_ROUTES.endpointEnable(id));
        toast.success(t('integrations.webhooks.enabled'));
      }
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  const remove = async () => {
    if (!data) return;
    const ok = await confirm({
      title: t('integrations.webhooks.deleteTitle', { name: data.name }),
      body: t('integrations.webhooks.deleteBody'),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(WEBHOOKS_ROUTES.endpoint(id));
      toast.success(t('integrations.webhooks.deleted'));
      dispatch(invalidateWebhookEndpoint(id));
      onClose();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  const sendTest = async () => {
    try {
      await api.post(WEBHOOKS_ROUTES.endpointTest(id));
      toast.success(t('integrations.webhooks.testSent'));
      reload();
      dispatch(invalidateWebhookDeliveries(id));
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={data?.name ?? t('integrations.webhooks.endpointTitle')}
      description={data?.url}
      className="max-w-3xl"
      footer={
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            {data && canManage && (
              <Button variant="outline" onClick={() => void toggleEnabled()}>
                {data.status === 'ACTIVE'
                  ? t('integrations.webhooks.disable')
                  : t('integrations.webhooks.enable')}
              </Button>
            )}
            {data && canManage && (
              <Button variant="danger" onClick={() => void remove()}>
                {t('common.delete')}
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('integrations.states.loadFailed'))} onRetry={reload} />
      )}
      {data && (
        <div className="max-h-[70vh] space-y-6 overflow-y-auto pe-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForWebhookEndpoint(data.status)}>
              {t(`integrations.webhookEndpointStatus.${data.status}`)}
            </Badge>
            <span className="text-sm text-ink-muted">
              {t('integrations.webhooks.secretVersion', { version: data.secretVersion })}
            </span>
          </div>

          {/*
              The auto-disabled state says plainly why, and what to do — the
              exact sentence `disabledReason` (delivery-policy.ts's
              `disabledReason()`) writes, naming the host, the dead-letter
              count and the last error. `DISABLED` (a person switched it off)
              gets its own, quieter banner rather than sharing this one, so
              "the platform did this" and "you did this" read differently at
              a glance.
          */}
          {data.status === 'DISABLED_FAILING' && (
            <div
              role="alert"
              className="space-y-2 rounded-lg border border-red-300 bg-red-50/70 p-3 text-sm dark:border-red-900 dark:bg-red-950/30"
            >
              <p className="font-medium text-red-700 dark:text-red-300">
                {t('integrations.webhooks.autoDisabledTitle')}
              </p>
              <p>{data.disabledReason}</p>
              <p className="text-xs text-ink-muted">{t('integrations.webhooks.autoDisabledNextSteps')}</p>
              {canManage && (
                <Button size="sm" onClick={() => void toggleEnabled()}>
                  {t('integrations.webhooks.enable')}
                </Button>
              )}
            </div>
          )}
          {data.status === 'DISABLED' && (
            <div className="rounded-lg border p-3 text-sm text-ink-muted">
              {t('integrations.webhooks.manuallyDisabled')}
            </div>
          )}

          <OverviewSection endpointId={id} endpoint={data} onSaved={reload} />

          <section className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">{t('integrations.webhooks.sectionSecret')}</h3>
              <div className="flex gap-2">
                {can(WEBHOOKS_PERMISSIONS.manage) && (
                  <Button size="sm" variant="outline" onClick={() => void sendTest()}>
                    <Icon as={Send} />
                    {t('integrations.webhooks.sendTest')}
                  </Button>
                )}
                {can(WEBHOOKS_PERMISSIONS.rotateSecret) && (
                  <Button size="sm" variant="outline" onClick={() => setRotating(true)}>
                    <Icon as={KeyRound} />
                    {t('integrations.webhooks.rotateSecret')}
                  </Button>
                )}
              </div>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-ink-muted">{t('integrations.webhooks.secretRotatedAt')}</dt>
                <dd>{formatDateTime(data.secretRotatedAt, i18n.language)}</dd>
              </div>
              {data.previousSecretExpiresAt && (
                <div>
                  <dt className="text-ink-muted">{t('integrations.webhooks.previousSecretExpiresAt')}</dt>
                  <dd>{formatDateTime(data.previousSecretExpiresAt, i18n.language)}</dd>
                </div>
              )}
            </dl>
            {data.previousSecretExpiresAt && (
              <p className="text-xs text-ink-muted">{t('integrations.webhooks.rotationWindowActive')}</p>
            )}
          </section>

          <HealthSection endpointId={id} />
          <DeliveriesSection endpointId={id} endpointActive={data.status === 'ACTIVE'} />
        </div>
      )}

      {data && rotating && (
        <RotateSecretDialog
          endpointId={id}
          onClose={() => setRotating(false)}
          onRotated={(issued) => {
            setRotating(false);
            setIssuedSecret(issued);
            reload();
          }}
        />
      )}

      <SecretRevealDialog
        open={issuedSecret !== null}
        secret={issuedSecret?.secret ?? null}
        title={t('integrations.webhooks.secretTitle')}
        intro={t('integrations.webhooks.rotatedIntro')}
        onClose={() => setIssuedSecret(null)}
      />
    </Dialog>
  );
}

// -------------------------------------------------------------- overview

function OverviewSection({
  endpointId,
  endpoint,
  onSaved,
}: {
  endpointId: string;
  endpoint: WebhookEndpoint;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can(WEBHOOKS_PERMISSIONS.manage);
  const branches = useBranches();
  const eventTypes = useWebhookEventTypesQuery();

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(endpoint.name);
  const [url, setUrl] = useState(endpoint.url);
  const [description, setDescription] = useState(endpoint.description ?? '');
  const [branchId, setBranchId] = useState(endpoint.branchId ?? '');
  const [types, setTypes] = useState<string[]>(endpoint.eventTypes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (type: string) =>
    setTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));

  const save = async () => {
    if (!name.trim() || !url.trim() || types.length === 0) {
      setError(t('integrations.webhooks.createValidation'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: UpdateWebhookEndpointBody = {
        name: name.trim(),
        url: url.trim(),
        description: description.trim(),
        eventTypes: types,
        branchId: branchId || null,
      };
      await api.patch(WEBHOOKS_ROUTES.endpoint(endpointId), body);
      toast.success(t('integrations.detail.configureSaved'));
      setEditing(false);
      onSaved();
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3 border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('integrations.detail.sectionConfigure')}</h3>
        {canManage && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            {t('common.edit')}
          </Button>
        )}
      </div>
      {!editing ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ink-muted">{t('integrations.webhooks.url')}</dt>
            <dd className="break-all">{endpoint.url}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">{t('integrations.common.branch')}</dt>
            <dd>
              {branches.find((b) => b.id === endpoint.branchId)?.name ??
                t('integrations.providers.wholeVenue')}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-ink-muted">{t('integrations.webhooks.eventTypes')}</dt>
            <dd>{endpoint.eventTypes.join(', ')}</dd>
          </div>
        </dl>
      ) : (
        <div className="space-y-3">
          <Field label={t('integrations.webhooks.name')} required>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </Field>
          <Field label={t('integrations.webhooks.url')} required>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} maxLength={2048} />
          </Field>
          <Field label={t('integrations.webhooks.description')}>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </Field>
          {branches.length > 0 && (
            <Field label={t('integrations.common.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('integrations.providers.wholeVenue')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t('integrations.webhooks.eventTypes')} required>
            <div className="max-h-52 space-y-1.5 overflow-y-auto rounded-lg border p-2">
              {(eventTypes.data ?? []).map((def) => (
                <label key={def.type} className="flex items-start gap-2 rounded p-1 text-sm hover:bg-hover">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={types.includes(def.type)}
                    onChange={() => toggle(def.type)}
                  />
                  <span>
                    <span className="font-mono">{def.type}</span>
                    <span className="block text-xs text-ink-muted">{def.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </Field>
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void save()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

// -------------------------------------------------------------- rotate

function RotateSecretDialog({
  endpointId,
  onClose,
  onRotated,
}: {
  endpointId: string;
  onClose: () => void;
  onRotated: (issued: WebhookEndpointWithSecret) => void;
}) {
  const { t } = useTranslation();
  const [overlapMinutes, setOverlapMinutes] = useState(WEBHOOK_ROTATION_DEFAULT_OVERLAP_MINUTES);
  const [immediate, setImmediate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: RotateWebhookSecretBody = { overlapMinutes: immediate ? 0 : overlapMinutes };
      const { data } = await api.post<WebhookEndpointWithSecret>(
        WEBHOOKS_ROUTES.endpointRotateSecret(endpointId),
        body,
      );
      onRotated(data);
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('integrations.webhooks.rotateSecret')}
      className="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('integrations.webhooks.rotateSecret')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/*
            ADR-0004: rotation sends BOTH signatures until the window closes,
            so swapping the stored secret is not an outage. Said plainly,
            because an operator who believes rotation breaks their receiver
            will never rotate — and secrets that "can be rotated" but are
            never rotated is exactly the failure this window exists to avoid.
        */}
        <p className="rounded-lg border border-sky-300 bg-sky-50/60 p-3 text-sm dark:border-sky-900 dark:bg-sky-950/30">
          {t('integrations.webhooks.rotationExplainer')}
        </p>
        <Field
          label={t('integrations.webhooks.overlapMinutes')}
          hint={t('integrations.webhooks.overlapMinutesHint')}
        >
          <Select
            disabled={immediate}
            value={String(overlapMinutes)}
            onChange={(e) => setOverlapMinutes(Number(e.target.value))}
          >
            <option value="60">{t('integrations.webhooks.overlapOneHour')}</option>
            <option value={String(WEBHOOK_ROTATION_DEFAULT_OVERLAP_MINUTES)}>
              {t('integrations.webhooks.overlapOneDay')}
            </option>
            <option value={String(7 * 24 * 60)}>{t('integrations.webhooks.overlapOneWeek')}</option>
            <option value={String(WEBHOOK_ROTATION_MAX_OVERLAP_MINUTES)}>
              {t('integrations.webhooks.overlapThirtyDays')}
            </option>
          </Select>
        </Field>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={immediate}
            onChange={(e) => setImmediate(e.target.checked)}
          />
          <span>
            <span className="font-medium">{t('integrations.webhooks.revokeImmediately')}</span>
            <span className="block text-xs text-ink-muted">
              {t('integrations.webhooks.revokeImmediatelyHint')}
            </span>
          </span>
        </label>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------- health

function HealthSection({ endpointId }: { endpointId: string }) {
  const { t } = useTranslation();
  const { data } = useWebhookEndpointHealthQuery(endpointId);
  if (!data) return null;
  return (
    <section className="space-y-3 border-t pt-4">
      <h3 className="text-sm font-semibold">{t('integrations.detail.sectionHealth')}</h3>
      <div className="flex flex-wrap gap-3 text-sm">
        {Object.entries(data.deliveries)
          .filter(([, count]) => count > 0)
          .map(([status, count]) => (
            <Badge key={status} tone={toneForWebhookDelivery(status)}>
              {t(`integrations.webhookDeliveryStatus.${status}`)}: {count}
            </Badge>
          ))}
        {Object.values(data.deliveries).every((c) => c === 0) && (
          <span className="text-ink-muted">{t('integrations.deliveries.noDeliveries')}</span>
        )}
      </div>
      {data.lastError && (
        <p className="rounded-lg border border-red-300 bg-red-50/60 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {data.lastError}
        </p>
      )}
    </section>
  );
}

// ----------------------------------------------------------- deliveries

function DeliveriesSection({
  endpointId,
  endpointActive,
}: {
  endpointId: string;
  endpointActive: boolean;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();
  const canReplay = can(WEBHOOKS_PERMISSIONS.replay);

  const [deadLettersOnly, setDeadLettersOnly] = useState(false);
  const [viewingPayload, setViewingPayload] = useState<string | null>(null);
  const { data, isFetching, isError, error, refetch } = useWebhookDeliveriesQuery({
    endpointId,
    limit: 25,
    ...(deadLettersOnly ? { status: 'DEAD_LETTERED' } : {}),
  });
  const payload = useWebhookDeliveryQuery(viewingPayload ?? '', { skip: !viewingPayload });

  const reload = () => dispatch(invalidateWebhookDeliveries(endpointId));

  const replay = async (deliveryId: string) => {
    try {
      await api.post(WEBHOOKS_ROUTES.deliveryReplay(deliveryId));
      toast.success(t('integrations.deliveries.replayed'));
      reload();
    } catch (requestError) {
      const code = errorCode(requestError);
      if (code === 'WEBHOOK_ENDPOINT_DISABLED') {
        toast.error(t('integrations.webhooks.replayNeedsActiveEndpoint'));
      } else {
        toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
      }
    }
  };

  return (
    <section className="space-y-3 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('integrations.detail.sectionDeliveries')}</h3>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={deadLettersOnly}
            onChange={(e) => setDeadLettersOnly(e.target.checked)}
          />
          {t('integrations.deliveries.deadLettersOnly')}
        </label>
      </div>

      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('integrations.states.loadFailed'))} onRetry={refetch} />
      )}
      {data && data.items.length === 0 && (
        <p className="py-6 text-center text-sm text-ink-muted">
          {deadLettersOnly
            ? t('integrations.deliveries.noDeadLetters')
            : t('integrations.deliveries.noDeliveries')}
        </p>
      )}
      {!endpointActive && canReplay && (data?.items.length ?? 0) > 0 && (
        <p className="text-xs text-ink-muted">{t('integrations.webhooks.replayNeedsActiveEndpoint')}</p>
      )}
      {data && data.items.length > 0 && (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.webhooks.eventType')}</TH>
                <TH>{t('integrations.deliveries.event')}</TH>
                <TH>{t('integrations.deliveries.status')}</TH>
                <TH>{t('integrations.deliveries.attempts')}</TH>
                <TH>{t('integrations.webhooks.statusCode')}</TH>
                <TH className="text-end">{t('common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {data.items.map((d) => (
                <TR key={d.id}>
                  <TD className="text-sm">{d.eventType}</TD>
                  {/* The stable dedupe id, surfaced verbatim — ADR-0004: a receiver dedupes on `event.id`. */}
                  <TD className="max-w-[10rem] truncate font-mono text-xs text-ink-muted" title={d.eventId}>
                    {d.eventId}
                  </TD>
                  <TD>
                    <Badge tone={toneForWebhookDelivery(d.status)}>
                      {t(`integrations.webhookDeliveryStatus.${d.status}`)}
                    </Badge>
                  </TD>
                  <TD className="text-sm tabular-nums">{d.attempts}</TD>
                  <TD className="text-sm tabular-nums text-ink-muted">{d.lastStatusCode ?? '—'}</TD>
                  <TD className="text-end">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setViewingPayload(d.id)}>
                        {t('integrations.webhooks.viewPayload')}
                      </Button>
                      {canReplay && endpointActive && (
                        <Button size="sm" variant="outline" onClick={() => void replay(d.id)}>
                          {t('integrations.deliveries.replay')}
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Dialog
        open={viewingPayload !== null}
        onClose={() => setViewingPayload(null)}
        title={t('integrations.webhooks.payloadTitle')}
        description={t('integrations.webhooks.payloadHint')}
        className="max-w-lg"
        footer={
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setViewingPayload(null)}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        {payload.data && (
          <pre className="max-h-96 overflow-auto rounded-lg border bg-subtle p-3 text-xs">
            {JSON.stringify(payload.data.payload, null, 2)}
          </pre>
        )}
      </Dialog>
    </section>
  );
}
